/**
 * Memoized aggregation across all consultas analyses.
 *
 * Filters: optional date range and `done` status.
 */
import { useMemo } from "react";
import { useConsultasAI } from "./useConsultasAI";
import { aggregateConsultas, type ConsultasAggregate } from "../lib/aggregateConsultas";

export interface UseConsultasAggregateOptions {
  onlyDone?: boolean;
  fromDate?: Date | null;
  toDate?: Date | null;
}

export interface UseConsultasAggregateResult {
  aggregate: ConsultasAggregate | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useConsultasAggregate(
  opts: UseConsultasAggregateOptions = {},
): UseConsultasAggregateResult {
  const { onlyDone = true, fromDate = null, toDate = null } = opts;
  const query = useConsultasAI();

  const aggregate = useMemo(() => {
    if (!query.data) return null;
    const filtered = query.data.filter((c) => {
      if (onlyDone && c.jobStatus !== "done") return false;
      if (!c.analysisPayload) return false;
      if (fromDate || toDate) {
        const d = new Date(c.createdAt);
        if (Number.isNaN(d.getTime())) return false;
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
      }
      return true;
    });
    return aggregateConsultas(filtered);
  }, [query.data, onlyDone, fromDate, toDate]);

  return {
    aggregate,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    refetch: () => void query.refetch(),
  };
}
